'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

import { HOTEL_PAST_SEARCH } from '@constants/constants';
import { generateSearchRooms } from '@components/views/landing/components/booker/common/bookerUtils';

interface CardLinkProps {
  href?: string;
  children: React.ReactNode;
  'data-testid'?: string;
  prefetch?: boolean;
  key?: string | number;
  className?: string;
}

function buildPatchedHref(baseHref: string) {
  try {
    const pastItemsRaw = localStorage.getItem(HOTEL_PAST_SEARCH);
    if (!pastItemsRaw) return baseHref;

    const pastItems = JSON.parse(pastItemsRaw);
    if (!pastItems.length) return baseHref;

    const lastSearch = pastItems[0];
    if (!lastSearch.in || !lastSearch.out) return baseHref;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const inDate = new Date(lastSearch.in + 'T00:00:00');
    const outDate = new Date(lastSearch.out + 'T00:00:00');
    if (inDate < today || outDate < today) return baseHref;

    const rooms = generateSearchRooms(lastSearch.rooms, true);
    const queryParams = new URLSearchParams({
      in: lastSearch.in,
      out: lastSearch.out,
      ...(lastSearch.rooms?.length ? { room: rooms } : {}),
    }).toString();

    return `${baseHref}?${queryParams}`;
  } catch {
    return baseHref;
  }
}

export const PatchedLink = ({
  key,
  href,
  children,
  className,
  prefetch = false,
}: CardLinkProps) => {
  const searchParams = useSearchParams();
  const [finalHref, setFinalHref] = useState(href || '');

  useEffect(() => {
    if (!href) return;
    const addLastSearchParams = href.includes('/otel');
    const patched = addLastSearchParams ? buildPatchedHref(href) : href;
    setFinalHref(patched);
  }, [href, searchParams]);

  return (
    <Link key={key} href={finalHref} prefetch={prefetch} className={className}>
      {children}
    </Link>
  );
};
